import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import DischargeSummaryForm from "./DischargeSummaryForm";
import { createDischargeSummary, clearError } from "../../../store/slices/dischargeSummarySlice";
import { fetchPatient } from "../../../store/slices/patientsSlice";
import { fetchIntegratedDischargePlanningByCase } from "../../../store/slices/integratedAssessmentSlice";

// Split a stored CSV string into a trimmed array (empty -> [])
const csvToArray = (v) => (typeof v === "string" && v.trim() !== "" ? v.split(",").map((s) => s.trim()).filter(Boolean) : Array.isArray(v) ? v : []);

const AddDischargeSummary = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId, program, type_id } = state || {}; // ✅ Get type_id from navigation state

    // Redux state
    const { isLoading, error } = useSelector((state) => state.dischargeSummary);
    console.log("Navigation state:", { caseId, patientId, program, type_id });

    const [formData, setFormData] = useState({
        // Foreign Keys
        patient_id: patientId,
        case_id: caseId,
        program_master_id: program?.id || null,
        type_id: type_id || null,

        // Client Information
        client_name: "",
        CNIC: "",
        gender: "",
        date_of_birth: "",
        program: program?.name,
        admission_date: "",
        discharge_date: "",

        // Provisional Psychiatric Diagnosis
        primary_diagnosis: "",
        secondary_diagnosis: "",
        co_morbidities: "",
        presenting_symptoms: "",

        // Discharge Planning
        discharge_types: [],

        // Services Provided
        service_provided: [],

        // Clinical Reports (remarks + attachment per report)
        psychiatrist_remarks: "",
        psychiatrist_attachment: null,
        care_manager_remarks: "",
        care_manager_attachment: null,
        primary_care_remarks: "",
        primary_care_attachment: null,

        // Progress since admission
        clinical_progress: [],
        mental_state_at_discharge: [],
        complications: "",

        // Individualized Rehabilitation Plan, Progress & Outcomes
        irp_domains: [],
        short_term_goals: "",
        long_term_goals: "",
        current_progress: [],
        progress_achieved_text: "",
        progress_in_progress_text: "",
        progress_not_achieved_text: "",
        additional_responsibilities: "",
        outcomes: "",

        // Medications
        discharge_medications: [
            { name: "", dosage: "", frequency: "", route: "" }
        ],

        // Instructions / Family Counseling
        instructions: [],
        family_counseling: [],

        // Home Care & Future Support Plan
        teaching_provided_to: [],
        recovery_goals: "",
        possible_relapse_signs: "",
        injections: "",
        risk_assessment: "",
        who_to_contact: "",
        where_to_go_crisis: "",
        follow_up_psychiatrist: "",
        physical_health_needs: "",
        special_instructions: "",
        referred_for: [],
        referred_for_other_text: "",
        next_of_kin_informed: "",
        emergency_contact_provided: "",
        safety_care_plan: "",

        // Final Section
        trh_recommendations: "",

        // Signatures (name + uploaded image)
        consultant_psychiatrist_name: "",
        prp_name: "",
        guardian_name: "",
        client_sign_name: "",
        consultant_psychiatrist_signature: null,
        prp_signature: null,
        guardian_signature: null,
        client_signature: null,
    });

    // Clear errors on component mount
    useEffect(() => {
        dispatch(clearError());
    }, [dispatch]);

    useEffect(() => {
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        setFormData((prev) => ({
                            ...prev,
                            client_name: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                            CNIC: patient.cnic || "",
                            gender: patient.gender || "",
                            date_of_birth: patient.dob ? patient.dob.split("T")[0] : "",
                        }));
                    }
                })
                .catch((err) => console.error("Failed to fetch patient details:", err));
        }
    }, [dispatch, patientId]);

    // Auto-fill Discharge Planning data from the Integrated Assessment of the SAME case.
    // Only the matching Discharge Planning fields are pulled; empty values are skipped so
    // nothing gets blanked out. "Day Rehab" transition maps to DS value "Day Care".
    useEffect(() => {
        if (!caseId && !patientId) return;
        dispatch(fetchIntegratedDischargePlanningByCase({ patientId, caseId }))
            .unwrap()
            .then((dp) => {
                if (!dp) return;
                const setIf = (obj, key, value) => {
                    if (value === null || value === undefined || value === "") return;
                    if (Array.isArray(value) && value.length === 0) return;
                    obj[key] = value;
                };
                // Transition values: Integrated stores "Day Rehab", DS stores "Day Care"
                const transition = csvToArray(dp.transition_for).map((v) => (v === "Day Rehab" ? "Day Care" : v));

                const patch = {};
                setIf(patch, "recovery_goals", dp.recovery_goals);
                setIf(patch, "possible_relapse_signs", dp.possible_relapse_signs);
                setIf(patch, "who_to_contact", dp.who_to_contact);
                setIf(patch, "where_to_go_crisis", dp.where_to_go_crisis);
                setIf(patch, "physical_health_needs", dp.physical_health_needs);
                setIf(patch, "special_instructions", dp.special_instructions);
                setIf(patch, "follow_up_psychiatrist", dp.followup_psychiatrist_date);
                setIf(patch, "teaching_provided_to", csvToArray(dp.education_provided_to));
                setIf(patch, "referred_for", transition);
                setIf(patch, "referred_for_other_text", dp.transition_for_other);

                if (Object.keys(patch).length > 0) {
                    // Only fill fields the user hasn't already touched (still at their empty default).
                    setFormData((prev) => {
                        const merged = { ...prev };
                        Object.entries(patch).forEach(([k, v]) => {
                            const cur = prev[k];
                            const isEmpty = cur === "" || cur === null || cur === undefined || (Array.isArray(cur) && cur.length === 0);
                            if (isEmpty) merged[k] = v;
                        });
                        return merged;
                    });
                }
            })
            .catch((err) => console.error("Failed to prefill from Integrated Assessment:", err));
    }, [dispatch, caseId, patientId]);

    // Handle form changes from child component
    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        // ----- 1. FILE INPUTS -----
        if (type === "file") {
            setFormData((prev) => ({
                ...prev,
                [name]: files[0] || null,
            }));
            return;
        }

        // ----- 2. MULTI-SELECT CHECKBOX GROUPS (arrays) -----
        const multiSelectFields = [
            "discharge_types",
            "teaching_provided_to",
            "service_provided",
            "clinical_progress",
            "mental_state_at_discharge",
            "irp_domains",
            "current_progress",
            "instructions",
            "family_counseling",
            "referred_for"
        ];

        if (multiSelectFields.includes(name)) {
            setFormData((prev) => ({
                ...prev,
                [name]: checked
                    ? [...prev[name], value]
                    : prev[name].filter((v) => v !== value),
            }));
            return;
        }

        // ----- 3. BOOLEAN CHECKBOXES (true/false) -----
        const booleanCheckboxes = [
            "referred_to_other",
            "referred_for_daycare",
            "referred_for_home_visiting",
            "referred_for_other",
        ];

        if (booleanCheckboxes.includes(name)) {
            setFormData((prev) => ({
                ...prev,
                [name]: checked,
            }));
            return;
        }

        // ----- 4. NESTED OBJECT FIELDS (e.g., referredBy.xyz) -----
        if (name.startsWith("referredBy.")) {
            const key = name.split(".")[1];
            setFormData((prev) => ({
                ...prev,
                referredBy: {
                    ...prev.referredBy,
                    [key]: value,
                }
            }));
            return;
        }

        // ----- 5. RADIO, TEXT, TEXTAREA, SELECT -----
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Handle medications array updates from child component
    const handleMedicationsChange = (medications) => {
        setFormData((prev) => ({
            ...prev,
            discharge_medications: medications,
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // ----- 1️⃣ Adjust conditional fields before submission -----
        const adjustedFormData = {
            ...formData,
            referred_for_other_text: formData.referred_for.includes("Other")
                ? formData.referred_for_other_text
                : null,
        };

        // ----- 2️⃣ Create FormData object for file uploads -----
        const submitData = new FormData();

        Object.keys(adjustedFormData).forEach(key => {
            if (key === 'discharge_medications') {
                const validMedications = adjustedFormData.discharge_medications.filter(med => med.name?.trim() !== "");
                if (validMedications.length > 0) {
                    validMedications.forEach((med, index) => {
                        submitData.append(`discharge_medications[${index}][name]`, med.name || '');
                        submitData.append(`discharge_medications[${index}][dosage]`, med.dosage || '');
                        submitData.append(`discharge_medications[${index}][frequency]`, med.frequency || '');
                        submitData.append(`discharge_medications[${index}][route]`, med.route || '');
                    });
                } else {
                    submitData.append(`discharge_medications[0][name]`, '');
                    submitData.append(`discharge_medications[0][dosage]`, '');
                    submitData.append(`discharge_medications[0][frequency]`, '');
                    submitData.append(`discharge_medications[0][route]`, '');
                }
            } else if (adjustedFormData[key] !== null && adjustedFormData[key] !== undefined && adjustedFormData[key] !== "") {
                submitData.append(key, adjustedFormData[key]);
            }
        });

        // Debug: Log FormData contents
        console.log("FormData contents:");
        for (let pair of submitData.entries()) {
            console.log(pair[0] + ': ', pair[1]);
        }

        try {
            const response = await dispatch(createDischargeSummary(submitData)).unwrap();

            Swal.fire({
                title: "Success!",
                text: response.message || "Discharge Summary created successfully.", // Backend message here
                icon: "success",
                timer: 3000, // Thoda sa time badha diya taake user replicate wala message parh sake
                showConfirmButton: false,
            });

            const res = response.payload || response;
            navigate(`/phase07/discharge-summary/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (error) {
            console.error("Submission error:", error);

            let errorMessage = "Failed to create Discharge Summary";
            if (typeof error === 'object' && error !== null) {
                const firstError = Object.values(error)[0]?.[0];
                if (firstError) {
                    errorMessage = firstError;
                }
            } else if (typeof error === 'string') {
                errorMessage = error;
            }

            Swal.fire({
                title: "Error!",
                text: errorMessage,
                icon: "error",
                confirmButtonText: "Try Again",
            });
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <DischargeSummaryForm
                                formData={formData}
                                handleChange={handleChange}
                                handleMedicationsChange={handleMedicationsChange}
                                isSubmitting={isLoading}
                                program={program}
                                typeId={type_id} // ✅ Pass type_id to form
                            />
                            <div className="row mt-4">
                                <div className="col-md-2 ms-auto">
                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100"
                                        disabled={isLoading}
                                    >
                                        <i className="ri-save-3-line me-1"></i>
                                        {isLoading ? "Saving..." : "Save"}
                                    </button>
                                </div>
                                <div className="col-md-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary w-100"
                                        onClick={() => navigate(`/cases/${caseId}/profile`, {
                                            state: { patientId, caseId }
                                        })}
                                        disabled={isLoading}
                                    >
                                        <i className="ri-arrow-left-line me-1"></i> Cancel
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddDischargeSummary;